import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, Modal, Select, useToast } from "@theg1group/ui";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../lib/api.ts";
import type { AddWalletInput, AddWalletResult, ChainInfo, ValidateResult } from "../lib/types.ts";

export function AddWalletModal({
	partnerId,
	open,
	onClose,
}: {
	partnerId: string;
	open: boolean;
	onClose: () => void;
}) {
	const qc = useQueryClient();
	const toast = useToast();
	const [chain, setChain] = useState("");
	const [address, setAddress] = useState("");
	const [label, setLabel] = useState("");
	const [validation, setValidation] = useState<ValidateResult | null>(null);
	const [validating, setValidating] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const chains = useQuery({
		queryKey: ["chains"],
		queryFn: () => api.get<ChainInfo[]>("/api/wallets/chains"),
		enabled: open,
	});

	useEffect(() => {
		if (!chain && chains.data?.length) {
			const first = chains.data.find((c) => c.enabled) ?? chains.data[0];
			if (first) setChain(first.id);
		}
	}, [chains.data, chain]);

	// Debounced validate
	useEffect(() => {
		const a = address.trim();
		if (!chain || a.length < 8) {
			setValidation(null);
			return;
		}
		let cancelled = false;
		setValidating(true);
		const t = setTimeout(async () => {
			try {
				const r = await api.post<ValidateResult>("/api/wallets/validate", { chain, address: a });
				if (!cancelled) setValidation(r);
			} catch (e) {
				if (!cancelled) setValidation({ ok: false, reason: String(e) });
			} finally {
				if (!cancelled) setValidating(false);
			}
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(t);
		};
	}, [chain, address]);

	const add = useMutation({
		mutationFn: (body: AddWalletInput) =>
			api.post<AddWalletResult>(`/api/partners/${partnerId}/wallets`, body),
		onSuccess: (r) => {
			toast.addToast(
				"success",
				r.traceJobId ? `Wallet added — trace ${r.traceJobId.slice(0, 8)}… started` : "Wallet added",
			);
			qc.invalidateQueries({ queryKey: ["partner", partnerId] });
			qc.invalidateQueries({ queryKey: ["partners"] });
			qc.invalidateQueries({ queryKey: ["traces"] });
			reset();
			onClose();
		},
		onError: (e) => {
			if (e instanceof ApiError && e.status === 400) {
				try {
					const body = JSON.parse(e.message) as { error?: string; reason?: string };
					setError(body.reason ?? body.error ?? e.message);
					return;
				} catch {
					// fallthrough
				}
			}
			setError(String(e));
		},
	});

	function reset() {
		setAddress("");
		setLabel("");
		setValidation(null);
		setError(null);
	}

	const selectedChain = chains.data?.find((c) => c.id === chain);
	const canSubmit =
		!!chain && address.trim().length > 0 && validation?.ok === true && !add.isPending;

	return (
		<Modal
			open={open}
			onClose={() => {
				reset();
				onClose();
			}}
			title="Add wallet"
			footer={
				<div className="flex justify-end gap-2">
					<Button
						variant="ghost"
						onClick={() => {
							reset();
							onClose();
						}}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!canSubmit}
						loading={add.isPending}
						onClick={() => {
							setError(null);
							add.mutate({
								chain,
								address: address.trim(),
								label: label.trim() || undefined,
							});
						}}
					>
						Add wallet
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<FormField
					label="Chain"
					htmlFor="aw-chain"
					hint={
						selectedChain && !selectedChain.adapterReady
							? "Adapter not ready yet — address is stored, history fetch will start once the adapter ships."
							: undefined
					}
				>
					<Select
						id="aw-chain"
						value={chain}
						onChange={(e) => setChain(e.target.value)}
						options={(chains.data ?? []).map((c) => ({
							value: c.id,
							label: `${c.name} (${c.nativeSymbol})${c.adapterReady ? "" : " · adapter pending"}`,
							disabled: !c.enabled,
						}))}
						placeholder={chains.isLoading ? "Loading chains…" : "Select chain"}
					/>
				</FormField>

				<FormField
					label="Address"
					htmlFor="aw-address"
					error={
						validation && !validation.ok
							? (validation.reason ?? "Invalid address")
							: (error ?? undefined)
					}
					hint={validating ? "Validating…" : undefined}
				>
					<Input
						id="aw-address"
						value={address}
						onChange={(e) => setAddress(e.target.value)}
						placeholder="Paste wallet address"
						className="font-mono"
						autoComplete="off"
						spellCheck={false}
					/>
				</FormField>

				{validation?.ok && (
					<div className="rounded-md border border-line bg-surface-2 p-2 text-xs">
						<div className="text-success font-medium mb-1">Valid address</div>
						<div className="font-mono break-all text-content">{validation.display}</div>
						{validation.normalized && validation.normalized !== validation.display && (
							<div className="font-mono break-all text-muted">{validation.normalized}</div>
						)}
						{validation.explorer && (
							<a
								href={validation.explorer}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-primary hover:underline mt-1"
							>
								Explorer <ExternalLink size={12} />
							</a>
						)}
					</div>
				)}

				<FormField label="Label" htmlFor="aw-label">
					<Input
						id="aw-label"
						value={label}
						onChange={(e) => setLabel(e.target.value)}
						placeholder="Optional note (e.g. main payout wallet)"
					/>
				</FormField>
				<div className="text-[11px] text-muted">
					Adding a wallet starts a shallow trace automatically; follow it on the{" "}
					<Link to="/traces" className="text-primary hover:underline">
						Traces
					</Link>{" "}
					page.
				</div>
			</div>
		</Modal>
	);
}
